import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ROLE_LABELS, hasCapability } from "@/lib/roles";
import FinanceAccessPanel from "@/components/access/FinanceAccessPanel";
import InventoryAccessPanel from "@/components/access/InventoryAccessPanel";
import {
  Plus, Search, Pencil, KeyRound, Power, PowerOff, Copy, ShieldCheck, ShieldOff,
  Users as UsersIcon, X as XIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useDebounce } from "@/lib/useDebounce";

const emptyCreate = { email: "", password: "", name: "", role: "teacher", phone: "" };

const Users = () => {
  const { user } = useAuth();
  const canManage = hasCapability(user, "user.manage");

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 200);
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [busy, setBusy] = useState(false);

  const [editForm, setEditForm] = useState(null);
  const [credsDlg, setCredsDlg] = useState(null); // {email, temporary_password, name}
  const [accessDlg, setAccessDlg] = useState(null);
  const [override, setOverride] = useState({ capability: "", effect: "grant", scope: "self", reason: "", expires_at: "" });
  const [capabilitySearch, setCapabilitySearch] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/auth/users"); setList(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (canManage) load(); }, [canManage]);

  const filtered = useMemo(() => {
    const s = dq.trim().toLowerCase();
    return list.filter((u) => {
      if (roleFilter !== "all") {
        const roles = u.roles || [u.role];
        if (!roles.includes(roleFilter)) return false;
      }
      if (statusFilter === "active" && u.account_status === "disabled") return false;
      if (statusFilter === "disabled" && u.account_status !== "disabled") return false;
      if (s && !(u.name || "").toLowerCase().includes(s) && !(u.email || "").toLowerCase().includes(s)) return false;
      return true;
    });
  }, [list, dq, roleFilter, statusFilter]);

  const counts = useMemo(() => {
    const c = { total: list.length, parent: 0, teacher: 0, staff: 0, active: 0, disabled: 0 };
    list.forEach((u) => {
      const roles = u.roles || [u.role];
      if (roles.includes("parent")) c.parent++;
      if (roles.includes("teacher")) c.teacher++;
      if (roles.some((r) => ["director", "receptionist", "accountant", "attendance_officer", "super_admin"].includes(r))) c.staff++;
      if (u.account_status === "disabled") c.disabled++; else c.active++;
    });
    return c;
  }, [list]);

  const create = async () => {
    if (!createForm.email || !createForm.password) return toast.error("Email and password required");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", createForm);
      setList((l) => [data, ...l]);
      setCreateOpen(false); setCreateForm(emptyCreate);
      toast.success(`Created ${data.email}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editForm) return;
    const { id, ...payload } = editForm;
    setBusy(true);
    try {
      const { data } = await api.patch(`/auth/users/${id}`, payload);
      setList((l) => l.map((u) => u.id === id ? { ...u, ...data } : u));
      toast.success("User updated");
      setEditForm(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.name || u.email}? They'll need the new temp password to sign in.`)) return;
    try {
      const { data } = await api.post(`/auth/users/${u.id}/reset-password`);
      setCredsDlg(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u) => {
    if (u.id === user?.id) return toast.error("You can't deactivate yourself");
    try {
      const { data } = await api.post(`/auth/users/${u.id}/toggle-active`);
      setList((l) => l.map((x) => x.id === u.id ? { ...x, account_status: data.account_status } : x));
      toast.success(`Account ${data.account_status}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openAccess = async (u) => {
    if (window.location.search.includes("inventory_access=1")) setCapabilitySearch("inventory.");
    try { const { data } = await api.get(`/organisation/users/${u.id}/access`); setAccessDlg({ user: u, ...data }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const saveOverride = async () => {
    try { await api.post(`/organisation/users/${accessDlg.user.id}/overrides`, { ...override, expires_at: override.expires_at || null }); toast.success("Access override recorded"); setOverride({ capability: "", effect: "grant", scope: "self", reason: "", expires_at: "" }); openAccess(accessDlg.user); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const revokeOverride = async (id) => {
    const reason = window.prompt("Reason for removing this access override"); if (!reason) return;
    try { await api.post(`/organisation/users/${accessDlg.user.id}/overrides/${id}/revoke`, { reason }); toast.success("Access override removed"); openAccess(accessDlg.user); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!canManage) return <div className="text-[#475569]">Only Super Admin and Director can view user accounts.</div>;

  return (
    <div data-testid="users-page">
      <PageHeader
        overline="Access Control"
        title="User Accounts"
        description="All logins across staff, parents and students. Create, edit, reset passwords, activate or deactivate accounts."
        actions={
          <Button onClick={() => { setCreateForm(emptyCreate); setCreateOpen(true); }} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="new-user-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New User
          </Button>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
        <UsageKpi label="Total" value={counts.total} tone="brand" />
        <UsageKpi label="Staff" value={counts.staff} />
        <UsageKpi label="Teachers" value={counts.teacher} />
        <UsageKpi label="Parents" value={counts.parent} />
        <UsageKpi label="Deactivated" value={counts.disabled} tone={counts.disabled > 0 ? "warn" : "neutral"} />
      </div>

      {/* Filter bar */}
      <Card className="border-[#E2E8F0] p-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9 h-9 bg-white" data-testid="users-search" />
            {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#475569] hover:text-[#1B2C5A]"><XIcon className="w-3.5 h-3.5" /></button>}
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="h-9 w-40 bg-white" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Roles</SelectItem>
              {Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-36 bg-white" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="disabled">Deactivated</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-[#475569] ml-auto">{filtered.length} of {list.length}</span>
        </div>
      </Card>

      {loading ? <div className="text-center py-8 text-sm text-[#475569]">Loading…</div>
        : filtered.length === 0 ? <EmptyState message="No users match these filters." testId="users-empty" />
          : (
            <Card className="border-[#E2E8F0] overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-[#FDFBF7]">
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role(s)</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.map((u) => {
                    const roles = u.roles || [u.role];
                    const disabled = u.account_status === "disabled";
                    return (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`} className={disabled ? "opacity-60" : ""}>
                        <TableCell>
                          <div className="flex items-center gap-2.5">
                            <span className="w-8 h-8 rounded-full bg-[#1B2C5A] text-[#9DCD3F] grid place-items-center text-xs font-display shrink-0">
                              {u.avatar_initials || (u.name?.[0] || "?").toUpperCase()}
                            </span>
                            <span className="font-medium text-[#1B2C5A]">{u.name || "—"}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {roles.map((r) => (
                              <span key={r} className="text-[10px] px-2 py-0.5 rounded-full bg-[#9DCD3F]/15 text-[#1B2C5A] font-medium">{ROLE_LABELS[r] || r}</span>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{u.phone || "—"}</TableCell>
                        <TableCell>
                          {disabled ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200"><ShieldOff className="w-3 h-3" /> Deactivated</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3" /> Active</span>
                          )}
                          {u.must_change_password && (
                            <span className="ml-1 inline-block text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200" title="Must change password on next login">first-login</span>
                          )}
                        </TableCell>
                        <TableCell className="text-[10px] text-[#475569]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => openAccess(u)} className="text-indigo-700" title="Effective access"><ShieldCheck className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditForm({ id: u.id, name: u.name || "", phone: u.phone || "", role: u.role, roles: u.roles || [u.role] })} className="text-[#1B2C5A]" data-testid={`user-edit-${u.id}`} title="Edit"><Pencil className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => resetPassword(u)} className="text-amber-700" data-testid={`user-reset-${u.id}`} title="Reset password"><KeyRound className="w-4 h-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} disabled={u.id === user?.id} className={disabled ? "text-emerald-700" : "text-red-700"} data-testid={`user-toggle-${u.id}`} title={disabled ? "Reactivate" : "Deactivate"}>
                              {disabled ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2"><UsersIcon className="w-4 h-4 text-[#9DCD3F]" /> Create User Account</DialogTitle></DialogHeader>
          <div className="grid gap-3 mt-2">
            <Fld label="Name"><Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="user-name" /></Fld>
            <Fld label="Email"><Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} data-testid="user-email" /></Fld>
            <Fld label="Password (min 8 chars)"><Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} data-testid="user-password" /></Fld>
            <Fld label="Role">
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Fld>
            <Fld label="Phone (optional)"><Input value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} /></Fld>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
            <Button onClick={create} disabled={busy} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="user-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!accessDlg} onOpenChange={(v) => !v && setAccessDlg(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Effective Access — {accessDlg?.user?.name}</DialogTitle></DialogHeader>
          {accessDlg && <div className="space-y-5">
            <section><h3 className="font-semibold">Permission sources</h3><div className="mt-2 grid gap-2 sm:grid-cols-2">{accessDlg.capability_sources.map((x,i)=><div key={`${x.capability}-${i}`} className="rounded border p-2 text-xs"><strong>{x.capability}</strong><div className="text-slate-500">{x.source_type} · {x.scope || "default scope"}{x.expires_at ? ` · until ${x.expires_at}` : ""}</div></div>)}</div></section>
            {accessDlg.duplicate_login_warning&&<section className="rounded border border-amber-300 bg-amber-50 p-4"><h3 className="font-semibold text-amber-900">Multiple login accounts are linked to this staff member.</h3><p className="text-xs text-amber-800">Review these accounts and designate the intended primary login administratively. No account is merged or deleted here.</p><div className="mt-2 grid gap-2">{accessDlg.related_logins.map(x=><div key={x.id} className="rounded border bg-white p-2 text-xs"><strong>{x.email}</strong> · {(x.roles||[]).filter(Boolean).join(", ")} · {x.status}{x.last_login ? ` · last login ${x.last_login}` : ""}</div>)}</div></section>}
            <FinanceAccessPanel userId={accessDlg.user.id} />
            <InventoryAccessPanel access={accessDlg} onRefresh={() => openAccess(accessDlg.user)} />
            <section className="rounded border p-4"><h3 className="font-semibold">Exceptional user override</h3><p className="text-xs text-slate-500">Use only for an audited exception. Position, assignment and delegation remain the normal authority sources. Finance permissions are managed in Finance access above.</p><Input className="mt-3" placeholder="Search permissions" value={capabilitySearch} onChange={e=>setCapabilitySearch(e.target.value)}/><div className="mt-3 grid gap-3 sm:grid-cols-2"><select className="rounded border p-2" value={override.capability} onChange={e=>{const capability=e.target.value;const meta=accessDlg.available_capabilities.find(x=>x.code===capability);setOverride({...override,capability,scope:meta?.allowed_scopes?.[0]||"self"});}}><option value="">Select permission</option>{accessDlg.available_capabilities.filter(x=>x.direct_override_allowed&&!x.code.startsWith("finance.")&&`${x.module} ${x.label} ${x.code}`.toLowerCase().includes(capabilitySearch.toLowerCase())).map(x=><option key={x.code} value={x.code}>{x.module} — {x.label}{x.risk==="high"?" ⚠ High risk":""}</option>)}</select><select className="rounded border p-2" value={override.scope} onChange={e=>setOverride({...override,scope:e.target.value})}>{(accessDlg.available_capabilities.find(x=>x.code===override.capability)?.allowed_scopes||["self"]).map(x=><option key={x}>{x.replaceAll("_"," ")}</option>)}</select><Input type="datetime-local" value={override.expires_at} onChange={e=>setOverride({...override,expires_at:e.target.value})}/><Input placeholder="Required audit reason" value={override.reason} onChange={e=>setOverride({...override,reason:e.target.value})}/></div>{override.capability&&<p className="mt-2 text-xs text-slate-500">{accessDlg.available_capabilities.find(x=>x.code===override.capability)?.description} <code>{override.capability}</code></p>}<Button className="mt-3" disabled={!override.capability||override.reason.trim().length<5} onClick={saveOverride}>Grant audited override</Button></section>
            <section><h3 className="font-semibold">Direct overrides</h3>{accessDlg.direct_overrides.map(x=><div key={x.id} className="mt-2 flex items-center justify-between rounded border p-2 text-sm"><span>{x.capability} · {x.scope}{x.revoked_at ? " · revoked" : ""}</span>{!x.revoked_at&&<Button size="sm" variant="outline" onClick={()=>revokeOverride(x.id)}>Revoke</Button>}</div>)}</section>
          </div>}
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editForm} onOpenChange={(v) => !v && setEditForm(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2"><Pencil className="w-4 h-4 text-[#9DCD3F]" /> Edit User</DialogTitle></DialogHeader>
          {editForm && (
            <div className="grid gap-3 mt-2">
              <Fld label="Name"><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" /></Fld>
              <Fld label="Phone"><Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-phone" /></Fld>
              <Fld label="Primary Role">
                <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v, roles: [v, ...(editForm.roles || []).filter((r) => r !== v)] })}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Fld>
              <div>
                <Label className="text-[#1B2C5A] text-sm">Extra Roles (multi)</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {Object.entries(ROLE_LABELS).map(([k, v]) => {
                    const active = (editForm.roles || []).includes(k);
                    return (
                      <button key={k} onClick={() => setEditForm({ ...editForm, roles: active ? editForm.roles.filter((x) => x !== k) : [...editForm.roles, k] })}
                        className={"text-xs px-2 py-1 rounded-full border transition " + (active ? "bg-[#9DCD3F] border-[#9DCD3F] text-[#1B2C5A]" : "bg-white border-[#E2E8F0] text-[#475569] hover:border-[#9DCD3F]")}
                        data-testid={`edit-role-chip-${k}`}
                      >{v}</button>
                    );
                  })}
                </div>
                <p className="text-[10px] text-[#475569] mt-1">The Primary Role above will always be included.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditForm(null)} disabled={busy}>Cancel</Button>
            <Button onClick={saveEdit} disabled={busy} className="bg-[#1B2C5A] hover:bg-[#14224A] text-[#FDFBF7]" data-testid="edit-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password credential display */}
      <Dialog open={!!credsDlg} onOpenChange={(v) => !v && setCredsDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-600" /> Password Reset</DialogTitle></DialogHeader>
          {credsDlg && (
            <div className="space-y-3" data-testid="user-creds-dialog">
              <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900">
                <strong>Copy this now.</strong> The temporary password will not be shown again. The user will be forced to change it on next login.
              </div>
              <div className="grid grid-cols-[100px_1fr] gap-2 items-center text-sm">
                <div className="text-xs text-[#475569] uppercase">Email</div><div className="text-[#1B2C5A] break-all">{credsDlg.email}</div>
                <div className="text-xs text-[#475569] uppercase">Password</div><div className="font-mono font-semibold text-[#1B2C5A]" data-testid="user-temp-pwd">{credsDlg.temporary_password}</div>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${credsDlg.email}\nTemporary Password: ${credsDlg.temporary_password}\nPortal: ${window.location.origin}/login`).then(() => toast.success("Copied"));
                }}
                className="bg-[#1B2C5A] hover:bg-[#14224A] text-white w-full"
                data-testid="user-creds-copy"
              ><Copy className="w-4 h-4 mr-1.5" /> Copy All</Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCredsDlg(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-[#1B2C5A] text-sm">{label}</Label>{children}</div>
);

const UsageKpi = ({ label, value, tone = "neutral" }) => {
  const toneCls = tone === "brand" ? "bg-[#9DCD3F]/10 border-[#9DCD3F]/30" : tone === "warn" ? "bg-amber-50 border-amber-200" : "bg-white border-[#E2E8F0]";
  return (
    <Card className={"p-3 border " + toneCls}>
      <div className="text-[10px] uppercase tracking-wider text-[#9DCD3F] font-medium">{label}</div>
      <div className="font-display text-xl text-[#1B2C5A] mt-0.5">{value}</div>
    </Card>
  );
};

export default Users;
